import type {ReactElement} from 'react'

import {LABELS} from '../constants/labels'
import type {IssueCommentPinnedEventFragment} from './IssueCommentPinnedEvent'
import {PinnedCommentLink, PinnedCommentRendering} from './IssueCommentPinnedEvent'
import {
  type IssueCommentUnpinnedEventFragment,
  UnpinnedCommentLink,
  UnpinnedCommentRendering,
} from './IssueCommentUnpinnedEvent'

type RolledupIssueCommentPinEventProps = {
  rollupGroup: Record<string, Array<IssueCommentPinnedEventFragment | IssueCommentUnpinnedEventFragment>>
}

export function RolledupIssueCommentPinEvent({rollupGroup}: RolledupIssueCommentPinEventProps): ReactElement {
  const pinnedEvents = (rollupGroup.IssueCommentPinnedEvent as IssueCommentPinnedEventFragment[]) || []
  const unpinnedEvents = (rollupGroup.IssueCommentUnpinnedEvent as IssueCommentUnpinnedEventFragment[]) || []
  const firstPinnedRef = pinnedEvents[0] ?? null
  const firstUnpinnedRef = unpinnedEvents[0] ?? null

  const firstPinnedData = firstPinnedRef
  const firstUnpinnedData = firstUnpinnedRef
  const pinnedTimestamp = parseTimestamp(firstPinnedData?.createdAt)
  const unpinnedTimestamp = parseTimestamp(firstUnpinnedData?.createdAt)

  const isSingleCommentPinAndUnpin =
    pinnedEvents.length === 1 &&
    unpinnedEvents.length === 1 &&
    firstPinnedData?.issueComment?.databaseId != null &&
    firstPinnedData.issueComment.databaseId === firstUnpinnedData?.issueComment?.databaseId

  if (isSingleCommentPinAndUnpin) {
    const pinnedOccursFirst =
      pinnedTimestamp !== null && unpinnedTimestamp !== null ? pinnedTimestamp <= unpinnedTimestamp : true
    const firstLeading = pinnedOccursFirst
      ? LABELS.timeline.issueCommentPinned.leading
      : LABELS.timeline.issueCommentUnpinned.leading
    const secondLeading = pinnedOccursFirst
      ? LABELS.timeline.issueCommentUnpinned.leading
      : LABELS.timeline.issueCommentPinned.leading
    const combinedCommentLink = pinnedEvents[0] ? (
      <PinnedCommentLink queryRef={pinnedEvents[0]} />
    ) : unpinnedEvents[0] ? (
      <UnpinnedCommentLink queryRef={unpinnedEvents[0]} />
    ) : (
      <span>{`a ${LABELS.timeline.issueCommentPinned.comment}`}</span>
    )

    return (
      <>
        {firstLeading} {LABELS.timeline.and} {secondLeading} {'a '}
        {combinedCommentLink}
      </>
    )
  }

  return (
    <>
      <PinnedCommentRendering queryRefs={pinnedEvents} rollup />
      {pinnedEvents.length > 0 && unpinnedEvents.length > 0 && ` ${LABELS.timeline.and} `}
      <UnpinnedCommentRendering queryRefs={unpinnedEvents} rollup />
    </>
  )
}

const parseTimestamp = (value?: string | null): number | null => {
  if (!value) {
    return null
  }

  const time = Date.parse(value)
  return Number.isNaN(time) ? null : time
}
